/**
 * FingerprintJS open-source singleton.
 * ------------------------------------
 * Initialises on import, exposes a synchronous `getFingerprintId()` returning
 * either the real visitor id (after a brief async warm-up) or a stable
 * placeholder. Safe to import anywhere in the React tree.
 */
import FingerprintJS from "@fingerprintjs/fingerprintjs";

let visitorId = null;
let ready = false;
let initStarted = false;
const startedAt = Date.now();
// Per-session placeholder so backend can still correlate the session even
// before fingerprint has resolved.
const placeholder = `FP_INIT_${startedAt}_${Math.random().toString(36).slice(2, 10)}`;

const init = () => {
  if (initStarted) return;
  initStarted = true;
  try {
    const fpPromise = FingerprintJS.load({ monitoring: false });
    fpPromise
      .then((agent) => agent.get())
      .then((result) => { visitorId = result.visitorId; ready = true; })
      .catch(() => { visitorId = `FP_ERROR_${startedAt}`; ready = true; });
  } catch (_e) {
    visitorId = `FP_ERROR_${startedAt}`;
    ready = true;
  }
};

init();

/** Synchronous accessor — returns the resolved visitor id, placeholder during
 * warm-up, or an error marker if the library failed to load. */
export const getFingerprintId = () => (ready && visitorId) ? visitorId : placeholder;

/** Awaitable — useful when you need the resolved id (not the placeholder). */
export const awaitFingerprintId = async (timeoutMs = 3000) => {
  const deadline = Date.now() + timeoutMs;
  while (!ready && Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 50));
  }
  return getFingerprintId();
};

export const isFingerprintReady = () => ready;
